import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CompanyApi } from '@app/services/company-api';
import { CompanyInterface } from '@app/interface/company';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import {
  PaginatedResponse,
  RecursiveInterface,
} from '@app/services/base-authenticated.service';
import { InvoiceApi } from '@app/services/invoice-api';
import {
  InvoiceInterface,
  PensionFundOptions,
  DocumentTypeOptions,
} from '@app/interface/invoice';
// import { LayoutComponent } from '@app/layout/layout.component';
import { MatDividerModule } from '@angular/material/divider';
import { ArubaService } from '@app/services/aruba.service';
import { BackHeaderComponent } from '@app/back-header/back-header.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatChipListbox, MatChipOption } from '@angular/material/chips';
import { MatChipsModule } from '@angular/material/chips';
import { LanguageOptions } from '@app/interface/language-options';
import { StatusOptions } from '@app/interface/status-options';
import { IvaCollectabilityOptions } from '@app/interface/iva-collectability-options';
import { PaymentMethodOptions } from '@app/interface/payment-method-options';
import { CurrencyOption } from '@app/interface/currency-options';
import { MatSelectModule } from '@angular/material/select';
import { ProductserviceApiService } from '@app/services/productservice-api.service';
import { ProductServiceInterface } from '@app/interface/product-service';
import { MatTableModule } from '@angular/material/table';
import { MatExpansionModule } from '@angular/material/expansion';
import { format, parse } from 'date-fns';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormControl } from '@angular/forms';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { DownloadConfirmationDialogComponent } from '@app/download-confirmation-dialog/download-confirmation-dialog.component';
import { LoadingDialogComponent } from '@app/loading-dialog/loading-dialog.component';
import { ConfirmInvoiceDialogComponent } from '@app/confirm-invoice-dialog/confirm-invoice-dialog.component';
import { ConfirmProgressiveNumberDialogComponent } from '@app/confirm-progressive-number-dialog/confirm-progressive-number-dialog.component';
import { ConfirmInvoiceCompanyDataDialogComponent } from '@app/confirm-invoice-company-data-dialog/confirm-invoice-company-data-dialog.component';
import { ConfirmEmailSendDialogComponent } from '@app/confirm-email-send-dialog/confirm-email-send-dialog.component';
import { Organizations } from '@app/interface/organizations';
import { OrganizationApi } from '@app/services/organization-api.service';
import { ChangeDetectorRef } from '@angular/core';
import { StatusInfoDialogComponent } from '@app/status-info-dialog/status-info-dialog.component';
import { AtecoCodeApiService } from '@app/services/ateco-code-api.service';
import { AtecoCodeInterface } from '@app/interface/ateco-code';
import { AtecoCodeSelectorDialogComponent } from '@app/ateco-code-selector-dialog/ateco-code-selector-dialog.component';
import { QuickInvoiceTransferService } from '@app/services/quick-invoice-transfer.service';

@Component({
  selector: 'izy-invoice-detail',
  templateUrl: './invoice-detail.component.html',
  styleUrl: './invoice-detail.component.scss',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    // LayoutComponent,
    MatDividerModule,
    BackHeaderComponent,
    MatDatepickerModule,
    MatTooltipModule,
    MatIconModule,
    MatChipsModule,
    MatSelectModule,
    MatTableModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
  ],
  providers: [
    InvoiceApi,
    MatSnackBar,
    CompanyApi,
    { provide: MAT_DATE_LOCALE, useValue: 'it-IT' },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'DD/MM/YYYY',
        },
        display: {
          dateInput: 'DD/MM/YYYY',
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM YYYY',
        },
      },
    },
  ],
  standalone: true,
})
export class InvoiceDetailComponent {
  invoiceForm: FormGroup;
  invoiceId: number;
  isLoading = true;
  currentInvoice: InvoiceInterface | null = null;

  organization_data: Organizations | null = null;
  ateco_code_data: AtecoCodeInterface | null = null;
  atecoCodes: AtecoCodeInterface[] = []; // useful if the user wants to change it
  atecoCode: number = 0;

  revenueStampOption: 'none' | 'customer' | 'me' = 'none';

  showDelay = new FormControl(700);
  hideDelay = new FormControl(1000);

  // for readonly (to be able to modify drafts)
  public isEditMode = false;
  get shouldBeReadonly(): boolean {
    return (
      this.invoiceForm.get('is_draft')?.value === false || !this.isEditMode
    );
  }

  linkedCompany: {
    id: number;
    company_name: string;
    iva_number: string;
    address: string;
    addressee_code: string;
  } | null = null;
  company_name: string | undefined;
  company_address: string | undefined;
  company_iva_number: string | undefined;
  company_addressee_code: string | undefined;
  company_id!: number;

  // Product services properties
  productServices: ProductServiceInterface[] = [];
  displayedColumns: string[] = [
    'name',
    'quantity',
    'units',
    'gross_amount',
    'discount',
    'iva_percentage',
    'iva_amount',
    'final_amount',
  ];
  isLoadingProducts = false;

  // dict:
  currencyOptions: CurrencyOption[] = [
    { code: 'USD', name: 'US Dollar' },
    { code: 'EUR', name: 'Euro' },
    { code: 'GBP', name: 'British Pound' },
    { code: 'JPY', name: 'Japanese Yen' },
    { code: 'CAD', name: 'Canadian Dollar' },
    { code: 'AUD', name: 'Australian Dollar' },
    { code: 'CNY', name: 'Chinese Yuan' },
    { code: 'INR', name: 'Indian Rupee' },
  ];

  paymentMethodOptions: PaymentMethodOptions[] = [
    { code: 'MP01', name: 'Contanti' },
    { code: 'MP02', name: 'Assegno' },
    { code: 'MP03', name: 'Assegno circolare' },
    { code: 'MP04', name: 'Contanti presso Tesoreria' },
    { code: 'MP05', name: 'Bonifico' },
    { code: 'MP06', name: 'Vaglia cambiario' },
    { code: 'MP07', name: 'Bollettino bancario' },
    { code: 'MP08', name: 'Carta di credito' },
    { code: 'MP09', name: 'RID' },
    { code: 'MP10', name: 'RID utenze' },
    { code: 'MP11', name: 'RID veloce' },
    { code: 'MP12', name: 'Riba' },
    { code: 'MP13', name: 'MAV' },
    { code: 'MP14', name: 'Quietanza erario stato' },
    { code: 'MP15', name: 'Giroconto su conti di contabilità speciale' },
    { code: 'MP16', name: 'Domiciliazione bancaria' },
    { code: 'MP17', name: 'Domiciliazione postale' },
  ];

  ivaCollectabilityOptions: IvaCollectabilityOptions[] = [
    { code: 'IMMEDIATA', name: 'Immediata' },
    { code: 'DIFFERITA', name: 'Differita' },
    { code: 'SPLIT', name: 'Split payment' },
  ];

  languageOptions: LanguageOptions[] = [
    { code: 'it', name: 'Italiano' },
    { code: 'en', name: 'Inglese' },
    { code: 'fr', name: 'Francese' },
    { code: 'de', name: 'Tedesco' },
    { code: 'es', name: 'Spagnolo' },
  ];

  pensionFundOptions: PensionFundOptions[] = [
    {
      code: 'TC01',
      name: 'Cassa nazionale previdenza e assistenza avvocati e procuratori legali',
    },
    { code: 'TC02', name: 'Cassa previdenza dottori commercialisti' },
    { code: 'TC03', name: 'Cassa previdenza e assistenza geometri' },
    {
      code: 'TC04',
      name: 'Cassa nazionale previdenza e assistenza ingegneri e architetti liberi professionisti',
    },
    { code: 'TC05', name: 'Cassa nazionale del notariato' },
    {
      code: 'TC06',
      name: 'Cassa nazionale previdenza e assistenza ragionieri e periti commerciali',
    },
    {
      code: 'TC07',
      name: 'Ente nazionale assistenza agenti e rappresentanti di commercio (ENASARCO)',
    },
    {
      code: 'TC08',
      name: 'Ente nazionale previdenza e assistenza consulenti del lavoro (ENPACL)',
    },
    {
      code: 'TC09',
      name: 'Ente nazionale previdenza e assistenza medici (ENPAM)',
    },
    {
      code: 'TC10',
      name: 'Ente nazionale previdenza e assistenza farmacisti (ENPAF)',
    },
    {
      code: 'TC11',
      name: 'Ente nazionale previdenza e assistenza veterinari (ENPAV)',
    },
    {
      code: 'TC12',
      name: "Ente nazionale previdenza e assistenza impiegati dell'agricoltura (ENPAIA)",
    },
    {
      code: 'TC13',
      name: 'Fondo previdenza impiegati imprese di spedizione e agenzie marittime',
    },
    {
      code: 'TC14',
      name: 'Istituto nazionale previdenza giornalisti italiani (INPGI)',
    },
    {
      code: 'TC15',
      name: 'Opera nazionale assistenza orfani sanitari italiani (ONAOSI)',
    },
    {
      code: 'TC16',
      name: 'Cassa autonoma assistenza integrativa giornalisti italiani (CASAGIT)',
    },
    {
      code: 'TC17',
      name: 'Ente previdenza periti industriali e periti industriali laureati (EPPI)',
    },
    {
      code: 'TC18',
      name: 'Ente previdenza e assistenza pluricategoriale (EPAP)',
    },
    {
      code: 'TC19',
      name: 'Ente nazionale previdenza e assistenza biologi (ENPAB)',
    },
    {
      code: 'TC20',
      name: 'Ente nazionale previdenza e assistenza professione infermieristica (ENPAPI)',
    },
    {
      code: 'TC21',
      name: 'Ente nazionale previdenza e assistenza psicologi (ENPAP)',
    },
    { code: 'TC22', name: 'INPS' },
  ];

  statusOptions: StatusOptions[] = [
    { code: 'TRASMESSA', name: 'Trasmessa' },
    { code: 'ATTESA', name: 'Atttesa' },
  ];

  documentTypeOptions: DocumentTypeOptions[] = [
    { code: 'TD01', name: 'Fattura' },
    { code: 'TD04', name: 'Nota di credito' },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private invoiceService: InvoiceApi,
    private companyService: CompanyApi,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private arubaService: ArubaService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private productServiceApi: ProductserviceApiService,
    private organizationService: OrganizationApi,
    private atecoCodeService: AtecoCodeApiService,
    private quickInvoiceTransferService: QuickInvoiceTransferService,
  ) {
    this.invoiceForm = this.fb.group({
      invoice_name: [''],
      emission_date: [''],
      payment_date: [''],
      progressive_number: [1],
      enumeration: 'Primary',
      organization_id: [1],
      company_id: [''], // , Validators.required],
      description: [''],
      description_internal: [''],
      articles_number: [1],
      gross_amount: [0.0],
      amount: [0.0],
      iva_percentage: [0],
      iva_amount: [0.0],
      discount: [0.0],
      discount_amount: [0.0],
      addressee_code: [''],
      addressee_name: [''],
      is_paid: [false],
      language: [''],
      currency: [''],
      p_a: [false],
      payment_method: [''],
      iban: [''],
      status: [''],
      revenue_stamp: [0.0],
      revenue_stamp_for_company: [false],
      iva_collectability: [''],
      revenge: [0.0],
      revenge_rate: [0.0],
      pension_fund: [0.0],
      pension_fund_rate: [0.0],
      taxable_amount: [0.0],
      withholding_advance_tax: [0.0],
      withholding_advance_tax_rate: [0.0],
      withholding_tax: [0.0],
      withholding_tax_rate: [0.0],
      pension_fund_type: [''],
      healthcare_invoice: [false],
      paper_invoice: [false],
      document_type: ['TD01'],
      notes: [''],
      is_draft: [false],
      is_deleted: [false],
      is_preventive: [false],
      ateco_code_id: [0],
    });

    this.invoiceId = +this.route.snapshot.params['id'];
  }

  ngOnInit(): void {
    this.loadInvoiceData();
    // this.loadCompany();
    this.getOrganization();
    // this.getAtecoCode();
    this.loadAtecoCodes();

    console.log(this.invoiceForm);
  }

  private getOrganization() {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    if (organization_id && !isNaN(organization_id)) {
      const filterParams = {
        organization_id: organization_id,
      };

      this.organizationService
        .getOrganization<Organizations>(organization_id, filterParams)
        .subscribe({
          next: (organization_data: Organizations) => {
            // console.log('Organization:', ateco_code_data);
            this.organization_data = organization_data;
            this.cdr.detectChanges();
          },
          error: (err: any) => {
            console.error('Error loading organization:', err);
            this.cdr.detectChanges();
          },
        });
    } else {
      console.error(
        'Could not extract valid organization ID from URL or route parameters',
      );
    }
  }

  private getAtecoCode() {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    if (organization_id && !isNaN(organization_id)) {
      // const atecoCode = this.invoiceForm.value.ateco_code_id;
      this.atecoCode = this.invoiceForm.get('ateco_code_id')?.value;

      const filterParams = {
        id: this.atecoCode,
      };

      console.log('atecoCode for this invoice', this.atecoCode);

      if (true) {
        this.atecoCodeService
          .getAtecoCodeDetails<any>(
            organization_id,
            filterParams,
            this.atecoCode,
          )
          .subscribe({
            next: (response) => {
              // console.log('Full response:', response);
              // Extract the results array from the paginated response
              if (response) {
                this.ateco_code_data = response;

                console.log('ateco_code_data:', this.ateco_code_data);
              } else {
                console.warn(
                  'No results found for Ateco code:',
                  this.atecoCode,
                );
              }

              this.cdr.detectChanges();
            },
            error: (err: any) => {
              console.error('Error loading Ateco code:', err);
              this.cdr.detectChanges();
            },
          });
      } else {
        console.error('No Ateco code provided');
      }
    } else {
      console.error(
        'Could not extract valid organization ID from URL or route parameters',
      );
    }
  }

  private loadAtecoCodes() {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    this.isLoading = true;

    this.atecoCodeService
      .getAllAtecoCodes<AtecoCodeInterface>(organization_id, {})
      .subscribe({
        next: (response: RecursiveInterface<AtecoCodeInterface>) => {
          if (response.completed) {
            // Store all ATECO codes in the component property
            this.atecoCodes = response.results;
          } else {
            // Optional: Show loading progress
            console.log(
              `Loaded ${response.results.length} saved ateco codes so far...`,
            );
            this.atecoCodes = response.results;
          }
          this.isLoading = false;
        },
        error: (err: any) => {
          this.isLoading = false;
          console.error('Error loading ateco codes:', err);
          this.showErrorMessage('Errore nel caricamento dei codici ATECO.');
        },
      });
  }

  private loadInvoiceData(): void {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    this.invoiceService
      .getAllInvoices<InvoiceInterface>(organization_id)
      .subscribe({
        next: (response: RecursiveInterface<InvoiceInterface>) => {
          const invoice = response.results.find((c) => c.id === this.invoiceId);
          if (invoice) {
            this.currentInvoice = invoice;
            this.invoiceForm.patchValue(invoice);

            // Set the company data from the invoice
            this.linkedCompany = invoice.company || null;
            this.company_id = invoice.company_id;
            this.company_name = invoice.company?.company_name;
            this.company_address = invoice.company?.address;
            this.company_iva_number = invoice.company?.iva_number;
            this.company_addressee_code = invoice.company?.addressee_code;

            this.isLoading = false;

            // Load product services after invoice data is loaded
            this.loadProductServices(organization_id);

            // get the ateco code
            this.getAtecoCode();
          } else {
            throw new Error('Invoice not found');
          }
        },
        error: (err: any) => {
          console.error('Error loading invoice:', err);
          this.showErrorMessage('Errore nel caricamento.');
          this.router.navigate(['/invoice_table']);
        },
      });
  }

  onRevenueStampCustomerChange(event: MatSlideToggleChange) {
    if (event.checked) {
      this.revenueStampOption = 'customer';
    } else if (this.revenueStampOption === 'customer') {
      this.revenueStampOption = 'none';
    }
  }

  onRevenueStampMeChange(event: MatSlideToggleChange) {
    if (event.checked) {
      this.revenueStampOption = 'me';
    } else if (this.revenueStampOption === 'me') {
      this.revenueStampOption = 'none';
    }
  }

  formatFormDate(controlName: string): string {
    const dateValue = this.invoiceForm.get(controlName)?.value;
    if (!dateValue) return '';

    // If it's already a string in dd/MM/yyyy format, return as is
    if (typeof dateValue === 'string' && dateValue.includes('/')) {
      return dateValue;
    }

    // Format the date
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';

    return date.toLocaleDateString('it-IT'); // dd/MM/yyyy
  }

  // for soring draft edits
  private formatDateForBackend(date: any): string {
    if (!date) return '';

    console.log('Formatting date:', date, 'Type:', typeof date);

    // If it's a Moment.js object (from Material Datepicker)
    if (date && typeof date === 'object' && date._isAMomentObject) {
      return date.format('YYYY-MM-DD');
    }

    // If it's already a Date object
    if (date instanceof Date) {
      return date.toISOString().split('T')[0]; // YYYY-MM-DD
    }

    // If it's a string, try to parse it
    if (typeof date === 'string') {
      // Handle Italian format (dd/MM/yyyy)
      if (date.includes('/')) {
        const [day, month, year] = date.split('/').map(Number);
        const parsedDate = new Date(year, month - 1, day);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate.toISOString().split('T')[0];
        }
      } else {
        // Handle other string formats
        const parsedDate = new Date(date);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate.toISOString().split('T')[0];
        }
      }
    }

    return '';
  }

  // private loadCompany(): void {
  //   const organization_id = Number(
  //     this.route.snapshot.paramMap.get('organizationId')
  //   );

  //   // const company_id = this.invoiceForm.value.company_id;
  //   // console.log(this.invoiceForm.value.company_id);
  //   // console.log(company_id);

  //   this.isLoading = true;

  //   console.log('check', this.company_id);

  //   this.companyService
  //     .getSpecificCompany<CompanyInterface>(organization_id, this.company_id)
  //     .subscribe({
  //       next: (response: any) => {
  //         this.linkedCompany = response; // or use find() with specific ID
  //         this.company_name = this.linkedCompany?.company_name;

  //         this.isLoading = false;
  //       },
  //       error: (err: any) => {
  //         this.isLoading = false;
  //         console.error('Error loading companies:', err);
  //         this.showErrorMessage('Errore nel caricamento dei clienti');
  //       },
  //     });
  // }

  private loadProductServices(organizationId: number): void {
    this.isLoadingProducts = true;

    this.productServiceApi
      .getAllProductsServices<ProductServiceInterface>(
        this.invoiceId,
        organizationId,
      )
      .subscribe({
        next: (response: RecursiveInterface<ProductServiceInterface>) => {
          // Ensure numeric fields are actually numbers
          this.productServices = response.results.map((product) => ({
            ...product,
            gross_amount: this.safeNumberConvert(product.gross_amount),
            iva_amount: this.safeNumberConvert(product.iva_amount),
            final_amount: this.safeNumberConvert(product.final_amount),
            quantity: this.safeNumberConvert(product.quantity),
            discount: this.safeNumberConvert(product.discount),
            iva_percentage: this.safeNumberConvert(product.iva_percentage),
          }));
          this.isLoadingProducts = false;
          console.log('Loaded product services:', this.productServices);
        },
        error: (err: any) => {
          console.error('Error loading product services:', err);
          this.isLoadingProducts = false;
          this.showErrorMessage('Errore nel caricamento dei prodotti/servizi');
        },
      });
  }

  enableEditMode(): void {
    // Rimuovi la readonly da tutti i campi che vuoi rendere modificabili
    this.invoiceForm.enable();

    // Mantieni readonly alcuni campi che non dovrebbero essere modificati
    const readOnlyFields = ['organization_id', 'company_id', 'is_deleted'];

    readOnlyFields.forEach((field) => {
      this.invoiceForm.get(field)?.disable();
    });
  }

  // checkFormValidity(): void {
  //   console.log('=== FORM VALIDITY CHECK ===');
  //   console.log('Form valid:', this.invoiceForm.valid);
  //   console.log('Form invalid:', this.invoiceForm.invalid);
  //   console.log('Form pristine:', this.invoiceForm.pristine);
  //   console.log('Form dirty:', this.invoiceForm.dirty);
  //   console.log('Form touched:', this.invoiceForm.touched);

  //   // Controlla ogni campo individualmente
  //   Object.keys(this.invoiceForm.controls).forEach((key) => {
  //     const control = this.invoiceForm.get(key);
  //     if (control && control.invalid) {
  //       console.log(`Campo "${key}":`, {
  //         valid: control.valid,
  //         invalid: control.invalid,
  //         errors: control.errors,
  //         value: control.value,
  //       });
  //     }
  //   });

  //   // Mostra tutti i valori del form
  //   console.log('Form values:', this.invoiceForm.value);
  // }

  // // metodi helper
  // private markAllFieldsAsTouched(): void {
  //   Object.keys(this.invoiceForm.controls).forEach((key) => {
  //     const control = this.invoiceForm.get(key);
  //     if (control) {
  //       control.markAsTouched();
  //     }
  //   });
  // }

  // private getInvalidFields(): string[] {
  //   const invalidFields: string[] = [];
  //   Object.keys(this.invoiceForm.controls).forEach((key) => {
  //     const control = this.invoiceForm.get(key);
  //     if (control && control.invalid) {
  //       invalidFields.push(key);
  //     }
  //   });
  //   return invalidFields;
  // }

  saveDraft(): void {
    // Prima verifica la validità
    // this.checkFormValidity();

    // if (this.invoiceForm.invalid) {
    //   // Mostra quali campi sono problematici
    //   this.markAllFieldsAsTouched();

    //   const errorFields = this.getInvalidFields();
    //   this.showErrorMessage(
    //     `Form non valido. Controlla i seguenti campi: ${errorFields.join(', ')}`
    //   );
    //   return;
    // }

    // console.log(this.invoiceForm);

    if (this.invoiceForm.invalid) {
      this.showErrorMessage('Per favore, compila tutti i campi obbligatori');
      return;
    }

    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    // Prepara i dati per l'aggiornamento
    const updateData = {
      ...this.invoiceForm.getRawValue(), // Usa getRawValue() per includere i campi disabilitati
      id: this.invoiceId,
      emission_date: this.formatDateForBackend(
        this.invoiceForm.get('emission_date')?.value,
      ),
      payment_date: this.formatDateForBackend(
        this.invoiceForm.get('payment_date')?.value,
      ),
    };

    this.isLoading = true;

    this.invoiceService
      .updateInvoiceDetails(organization_id, this.invoiceId, updateData)
      .subscribe({
        next: (updatedInvoice: InvoiceInterface) => {
          this.currentInvoice = updatedInvoice;
          this.invoiceForm.patchValue(updatedInvoice);
          this.invoiceForm.disable(); // Torna in modalità readonly

          this.showSuccessMessage('Bozza aggiornata con successo!');
          this.isLoading = false;
        },
        error: (err: any) => {
          console.error('Error updating draft:', err);
          this.showErrorMessage('Errore durante il salvataggio della bozza');
          this.isLoading = false;
        },
      });
  }

  cancelEdit(): void {
    // Ricarica i dati originali
    if (this.currentInvoice) {
      this.invoiceForm.patchValue(this.currentInvoice);
    }
    this.invoiceForm.disable(); // Torna in modalità readonly
  }

  // Helper method to safely convert to number
  private safeNumberConvert(value: any): number {
    if (value === null || value === undefined) return 0;

    // If it's already a number, return it
    if (typeof value === 'number') return value;

    // If it's a string, try to convert it
    if (typeof value === 'string') {
      // Remove any extra formatting that might cause issues
      const cleanValue = value.replace(/[^\d.,]/g, '').replace(',', '.');

      // Remove multiple decimal points (keep only the first one)
      const parts = cleanValue.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      } else {
        value = cleanValue;
      }

      const num = parseFloat(value);
      return isNaN(num) ? 0 : num;
    }

    return 0;
  }

  changeInvoiceAtecoCode() {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    const dialogRef = this.dialog.open(AtecoCodeSelectorDialogComponent, {
      width: '600px',
      maxHeight: '80vh',
      data: {
        atecoCodes: this.atecoCodes,
        currentAtecoCodeId: this.ateco_code_data?.id || null,
      },
    });

    dialogRef
      .afterClosed()
      .subscribe((selectedAtecoCode: AtecoCodeInterface | null) => {
        if (selectedAtecoCode) {
          this.isLoading = true;

          this.invoiceService
            .updateInvoiceDetails(organization_id, this.invoiceId, {
              ateco_code_id: selectedAtecoCode.id,
            })
            .subscribe({
              next: (updatedInvoice: any) => {
                // Update local data
                this.ateco_code_data = selectedAtecoCode;
                this.invoiceForm.patchValue({
                  ateco_code_id: selectedAtecoCode.id,
                });

                this.showSuccessMessage(
                  'Codice ATECO aggiornato con successo!',
                );
                this.isLoading = false;
                this.cdr.detectChanges();
              },
              error: (err: any) => {
                console.error('Error updating ATECO code:', err);
                this.showErrorMessage(
                  "Errore durante l'aggiornamento del codice ATECO.",
                );
                this.isLoading = false;
              },
            });
        }
      });
  }

  getIvaCollectabilityDisplay(value: string | null | undefined): string {
    if (value === null || value === undefined) return 'Non specificata';

    const collectabilityMap: Record<string, string> = {
      IMMEDIATA: 'Immediata',
      DIFFERITA: 'Differita',
      SPLIT: 'Split payment',
    };

    return collectabilityMap[value] || 'Non specificata';
  }

  get displayDocumentType(): string {
    const docTypeCode = this.invoiceForm.get('document_type')?.value;
    const found = this.documentTypeOptions.find(
      (option) => option.code === docTypeCode,
    );
    return found ? `${found.name}` : 'Seleziona tipo documento'; // (${found.code})
  }

  get displayPaymentMethodType(): string {
    const payTypeCode = this.invoiceForm.get('payment_method')?.value;
    const found = this.paymentMethodOptions.find(
      (option) => option.code === payTypeCode,
    );
    return found ? `${found.name} (${found.code})` : 'Seleziona tipo pagamento';
  }

  getLanguageDisplayName(): string {
    const langCode = this.invoiceForm.get('language')?.value;
    if (!langCode) return '';

    const langOption = this.languageOptions.find(
      (option) => option.code === langCode,
    );
    return langOption ? langOption.name : langCode;
  }

  getIvaCollectabilityDisplayName(): string {
    const ivaCode = this.invoiceForm.get('iva_collectability')?.value;
    if (!ivaCode) return '';

    const ivaOption = this.ivaCollectabilityOptions.find(
      (option) => option.code === ivaCode,
    );
    return ivaOption ? ivaOption.name : ivaCode;
  }

  getPensionFundDisplayName(): string {
    const pensionFundCode = this.invoiceForm.get('pension_fund_type')?.value;
    if (!pensionFundCode) return '';

    const pensionFundOption = this.pensionFundOptions.find(
      (option) => option.code === pensionFundCode,
    );
    return pensionFundOption ? pensionFundOption.name : pensionFundCode;
  }

  getBooleanDisplay(value: boolean | null | undefined): string {
    if (value === null || value === undefined) return 'Non specificato';
    return value ? 'Sì' : 'No';
  }

  // method to your InvoiceDetailComponent class
  getTotalProductServices(): number {
    return this.productServices.reduce(
      (total, product) => total + (product.final_amount || 0),
      0,
    );
  }

  duplicateInvoice(): void {
    if (!this.currentInvoice) {
      this.showErrorMessage(
        'Impossibile duplicare: dati fattura non disponibili',
      );
      return;
    }

    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    // this.revenueStampOption riflette solo i toggle dell'utente in questa
    // sessione (mai sincronizzato dai dati caricati): per il draft si ricava
    // direttamente dai campi persistiti della fattura da duplicare.
    const revenueStampOption: 'none' | 'customer' | 'me' =
      this.currentInvoice.revenue_stamp > 0
        ? this.currentInvoice.revenue_stamp_for_company
          ? 'customer'
          : 'me'
        : 'none';

    this.quickInvoiceTransferService.setInvoiceDuplicateDraft({
      invoice: this.currentInvoice,
      productServices: this.productServices,
      revenueStampOption,
    });

    this.router.navigate([`/${organization_id}/new_invoice`]).then(() => {
      this.showSuccessMessage('Dati trasferiti nella nuova fattura.');
    });
  }

  downloadPdf(): void {
    try {
      this.invoiceService.downloadInvoicePdf(this.invoiceId);
      this.showSuccessMessage(
        `La fattura è stata scaricata sul dispositivo con successo!`,
      );
    } catch (error) {
      this.showErrorMessage('Errore download PDF. Riprovare.');
    }
  }

  downloadXml(): void {
    try {
      this.invoiceService.downloadInvoiceXml(this.invoiceId);
      this.showSuccessMessage(
        `La fattura è stata scaricata sul dispositivo con successo!`,
      );
    } catch (error) {
      this.showErrorMessage('Errore download XML. Riprovare.');
    }
  }

  async publishDraft(): Promise<void> {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    // Check if it's actually a draft
    if (this.invoiceForm.get('is_draft')?.value !== true) {
      this.showErrorMessage('Solo le bozze possono essere pubblicate');
      return;
    }

    if (!this.currentInvoice) {
      this.showErrorMessage(
        'Impossibile pubblicare: dati fattura non disponibili',
      );
      return;
    }

    // Ask the user for confirmation
    const dialogRef = this.dialog.open(ConfirmInvoiceDialogComponent, {
      width: '90vw',
      maxWidth: '400px',
      height: '400px',
      data: {
        organization_source: this.organization_data?.source,
      },
    });

    const result = await dialogRef.afterClosed().toPromise();

    if (!result) {
      // User cancelled the dialog
      return;
    }

    const progressiveNumberDialogRef = this.dialog.open(
      ConfirmProgressiveNumberDialogComponent,
      {
        width: '90vw',
        maxWidth: '400px',
        height: '300px',
        data: {
          progressive_number: this.currentInvoice.progressive_number,
        },
      },
    );
    const progressiveNumberresult = await progressiveNumberDialogRef
      .afterClosed()
      .toPromise();

    if (!progressiveNumberresult) {
      // User cancelled the dialog
      return;
    }

    if (
      this.currentInvoice.company?.iva_number === '' ||
      this.currentInvoice.company?.c_f === ''
    ) {
      const checkCompanyDialogRef = this.dialog.open(
        ConfirmInvoiceCompanyDataDialogComponent,
        {
          width: '90vw',
          maxWidth: '400px',
          height: '400px',
          data: {
            c_f: this.currentInvoice.company?.c_f,
            iva_number: this.currentInvoice.company?.iva_number,
          },
        },
      );
      const checkCompanyResult = await checkCompanyDialogRef
        .afterClosed()
        .toPromise();

      if (!checkCompanyResult) {
        // User cancelled the dialog
        return;
      }
    }

    this.isLoading = true;

    try {
      // User confirmed - show loading dialog
      const loadingDialogRef = this.dialog.open(LoadingDialogComponent, {
        width: '90vw',
        maxWidth: '400px',
        height: '300px',
        disableClose: true, // Prevent closing during loading
      });

      // Call the backend to publish to Aruba
      const response = await this.invoiceService
        .publishToAruba(organization_id, this.invoiceId, this.currentInvoice)
        .toPromise();

      console.log(response);

      // Close loading dialog
      loadingDialogRef.close();

      // Check if the response indicates success
      if (response.success) {
        this.showSuccessMessage('Fattura pubblicata con successo!');

        // Update the form to reflect it's no longer a draft
        this.invoiceForm.patchValue({
          is_draft: false,
          status: 'TRASMESSA', // or whatever status you want
        });

        // Update current invoice
        this.currentInvoice.is_draft = false;

        // Optionally: Show download confirmation dialog
        this.openDownloadConfirmationDialog(this.currentInvoice);
      } else {
        // Handle different error types
        if (response.error === 'ARUBA_SYNC_DISABLED') {
          this.showErrorMessage(
            'Sincronizzazione disabilitata: operazione simulata',
          );
        } else {
          if (response.error === 'HEALTHCARE_INVOICE') {
            this.showErrorMessage(
              'Le fatture sanitarie non possono essere inviate.',
            );
          } else {
            this.showErrorMessage(
              response.message || 'Qualcosa è andato storto. Riprova.',
            );
          }
        }
      }
    } catch (error: any) {
      console.error('Error publishing invoice:', error);

      // Handle HTTP errors (4xx, 5xx)
      if (error.error && error.error.error) {
        this.showErrorMessage(error.error.error);
      } else if (error.error && error.error.message) {
        this.showErrorMessage(error.error.message);
      } else if (error.message) {
        this.showErrorMessage(error.message);
      } else {
        this.showErrorMessage('Errore di connessione durante la pubblicazione');
      }
    } finally {
      this.isLoading = false;
    }
  }

  openDownloadConfirmationDialog(invoice: InvoiceInterface): void {
    const downloadDialogRef = this.dialog.open(
      DownloadConfirmationDialogComponent,
      {
        width: '90vw',
        maxWidth: '500px',
        data: {
          title: `${this.getDocumentTypeName(
            invoice.document_type!,
          )} creata con successo!`,
          documentType: 'Il documento',
          invoiceId: invoice.id!,
          invoiceName: invoice.invoice_name,
          companyName: invoice.company.company_name,
          text: 'è stato creato correttamente.',
        },
      },
    );

    downloadDialogRef.afterClosed().subscribe((result) => {
      if (result && result.download) {
        this.downloadPdf();
      }
    });
  }

  openStatusInfoDialog() {
    const dialogRef = this.dialog.open(StatusInfoDialogComponent, {
      width: '90vw',
      maxWidth: '600px', // Slightly wider to accommodate the link
      height: '520px',
    });
  }

  handleInfoClick(event: MouseEvent) {
    event.stopPropagation(); // Previene la propagazione
    this.openStatusInfoDialog(); // Apre la dialog
  }

  getDocumentTypeName(code: string): string {
    const found = this.documentTypeOptions.find(
      (option) => option.code === code,
    );
    return found ? found.name : 'Documento';
  }

  formatCardAmount(
    amount: number | undefined,
    currency: string = 'EUR',
  ): string {
    // Handle undefined or null values
    if (amount == null) {
      return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(0);
    }

    const formatted = new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

    return formatted;
  }

  async sendInvoiceEmail(): Promise<void> {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    // Check if currentInvoice is not null
    if (!this.currentInvoice) {
      this.showErrorMessage('Nessuna fattura selezionata.');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmEmailSendDialogComponent, {
      width: '90vw',
      maxWidth: '400px',
      height: '400px',
      data: {
        email_address: this.currentInvoice.company.email_address,
        company_id: this.currentInvoice.company.id,
        organization_id: organization_id,
      },
    });
    const result = await dialogRef.afterClosed().toPromise();

    if (!result) {
      // User cancelled the dialog
      return;
    }

    try {
      this.invoiceService.sendEmailWithPDF(
        organization_id,
        this.currentInvoice?.id,
        this.currentInvoice,
      );
      this.showSuccessMessage(
        `La fattura è stata inviata al cliente con successo!`,
      );
    } catch (error) {
      this.showErrorMessage('Errore invio email con PDF.');
    }
  }

  onCancel(): void {
    const org_id = this.route.snapshot.paramMap.get('organizationId');
    this.router.navigate([`/${org_id}/invoices_table`]);
  }

  onCancelDraft(): void {
    const org_id = this.route.snapshot.paramMap.get('organizationId');
    this.router.navigate([`/${org_id}/drafts_table`]);
  }

  private showSuccessMessage(message: string): void {
    this.snackBar.open(message, 'Chiudi', {
      duration: 3000,
      panelClass: ['success-snackbar'],
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }

  private showErrorMessage(message: string): void {
    this.snackBar.open(message, 'Chiudi', {
      duration: 3000,
      panelClass: ['error-snackbar'],
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }
}
